import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
    collection, addDoc, onSnapshot, query, orderBy,
    where, Timestamp, doc, updateDoc, deleteDoc, increment
} from 'firebase/firestore';
import Select from 'react-select';
import Swal from 'sweetalert2';
import moment from 'moment';

function Customerledger() {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    // Form States
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
    const [amount, setAmount] = useState("");
    const [type, setType] = useState("Credit");
    const [payMode, setPayMode] = useState("Cash");
    const [description, setDescription] = useState("");

    const userId = auth.currentUser?.uid;
    const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

    // 1. Live track all customers
    useEffect(() => {
        if (!userId) return;
        const unsub = onSnapshot(query(collection(db, "credit_customers"), where("userId", "==", userId)), (snap) => {
            setCustomers(snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                label: `${d.data().name} (Limit: ₹${d.data().creditLimit || 0})`,
                value: d.id
            })));
        });
        return () => unsub();
    }, [userId]);

    // Find the live customer data from the array to prevent stale state issues
    const liveCustomerData = customers.find(c => c.id === selectedCustomer?.id);

    // 2. Live track transaction history with dynamic live calculation
    useEffect(() => {
        if (!selectedCustomer || !userId || !liveCustomerData) {
            setHistory([]);
            return;
        }

        const q = query(
            collection(db, "customer_transactions"),
            where("customerId", "==", selectedCustomer.id),
            orderBy("date", "asc"),
            orderBy("created", "asc")
        );

        const unsub = onSnapshot(q, (snap) => {
            // Calculate running balance starting from the opening balance
            let runningBal = parseFloat(liveCustomerData.openingBalance || 0);
            const data = snap.docs.map(d => {
                const item = { id: d.id, ...d.data() };
                if (item.type === "Credit") runningBal += item.amount;
                else runningBal -= item.amount;
                return { ...item, runningBal };
            });
            // Reverse for display so newest is on top
            setHistory(data.reverse());
        });
        return () => unsub();
    }, [selectedCustomer, userId, liveCustomerData?.openingBalance]);

    const handleAddTransaction = async (e) => {
        e.preventDefault();
        if (!selectedCustomer || !amount) return Toast.fire({ icon: 'error', title: 'Select Customer & Amount' });

        setLoading(true);
        const numAmount = parseFloat(amount);
        const balanceChange = type === "Credit" ? numAmount : -numAmount;

        try {
            await addDoc(collection(db, "customer_transactions"), {
                userId,
                customerId: selectedCustomer.id,
                customerName: liveCustomerData?.name || selectedCustomer.name,
                date,
                amount: numAmount,
                type,
                payMode,
                description,
                created: Timestamp.now()
            });

            await updateDoc(doc(db, "credit_customers", selectedCustomer.id), {
                currentBalance: increment(balanceChange)
            });

            Toast.fire({ icon: 'success', title: 'Transaction Recorded' });
            setAmount("");
            setDescription("");
        } catch (err) {
            Toast.fire({ icon: 'error', title: err.message });
        } finally {
            setLoading(false);
        }
    };

    const deleteTransaction = async (item) => {
        const result = await Swal.fire({ title: 'Delete Entry?', text: "Balance will be reverted!", icon: 'warning', showCancelButton: true });
        if (result.isConfirmed) {
            const revertChange = item.type === "Credit" ? -item.amount : item.amount;
            try {
                await deleteDoc(doc(db, "customer_transactions", item.id));
                await updateDoc(doc(db, "credit_customers", item.customerId), { currentBalance: increment(revertChange) });
                Toast.fire({ icon: 'success', title: 'Deleted' });
            } catch (err) {
                Toast.fire({ icon: 'error', title: err.message });
            }
        }
    };

    return (
        <div className="content-wrapper bg-light min-vh-100">
            <section className="content-header py-2 px-4">
                <div className="d-flex justify-content-between align-items-center bg-white p-2 shadow-sm rounded border">
                    <h5 className="m-0 font-weight-bold text-navy"><i className="fas fa-file-invoice-dollar mr-2"></i>Ledger</h5>
                    <div style={{ width: '300px' }}>
                        <Select options={customers} value={selectedCustomer} onChange={setSelectedCustomer} placeholder="Search Customer..." className="text-sm" />
                    </div>
                </div>
            </section>

            <section className="content px-3">
                <div className="row">
                    {/* COMPACT INPUT FORM */}
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-navy text-white py-2"><span className="small font-weight-bold">Add Entry</span></div>
                            <div className="card-body p-3">
                                <form onSubmit={handleAddTransaction}>
                                    <div className="form-group mb-2">
                                        <label className="text-xs font-weight-bold mb-1">Date</label>
                                        <input type="date" className="form-control form-control-sm" value={date} onChange={e => setDate(e.target.value)} />
                                    </div>
                                    <div className="form-group mb-2">
                                        <label className="text-xs font-weight-bold mb-1">Type</label>
                                        <div className="btn-group w-100">
                                            <button type="button" onClick={() => setType('Credit')} className={`btn btn-xs ${type === 'Credit' ? 'btn-danger' : 'btn-outline-danger'}`}>Udhari (-)</button>
                                            <button type="button" onClick={() => setType('Payment')} className={`btn btn-xs ${type === 'Payment' ? 'btn-success' : 'btn-outline-success'}`}>Jama (-)</button>
                                        </div>
                                    </div>
                                    <div className="form-group mb-2">
                                        <label className="text-xs font-weight-bold mb-1">Amount (₹)</label>
                                        <input type="number" className="form-control form-control-sm font-weight-bold" value={amount} onChange={e => setAmount(e.target.value)} required />
                                    </div>
                                    <div className="form-group mb-2">
                                        <label className="text-xs font-weight-bold mb-1">Mode</label>
                                        <select className="form-control form-control-sm" value={payMode} onChange={e => setPayMode(e.target.value)}>
                                            <option>Cash</option>
                                            <option>Online</option>
                                            <option>Cheque</option>
                                        </select>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-xs font-weight-bold mb-1">Description</label>
                                        <input className="form-control form-control-sm" placeholder="Bill no / Items" value={description} onChange={e => setDescription(e.target.value)} />
                                    </div>
                                    <button type="submit" disabled={loading || !selectedCustomer} className="btn btn-navy btn-sm btn-block font-weight-bold">
                                        {loading ? '...' : 'SAVE'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* COMPACT HISTORY TABLE */}
                    <div className="col-md-9">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                                <span className="small font-weight-bold text-navy">Transaction History</span>
                                {liveCustomerData && (
                                    <div className="badge badge-danger p-2">
                                        TOTAL DUE: ₹ {(liveCustomerData.currentBalance || 0).toLocaleString()}
                                    </div>
                                )}
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ maxHeight: '70vh' }}>
                                    <table className="table table-sm table-hover text-sm m-0">
                                        <thead className="bg-light">
                                            <tr className="text-xs">
                                                <th className="text-center" style={{ width: '40px' }}>Sr.</th>
                                                <th>Date</th>
                                                <th>Particulars</th>
                                                <th className="text-right">Udhari</th>
                                                <th className="text-right">Jama</th>
                                                <th className="text-right">Balance</th>
                                                <th className="text-center">#</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.length > 0 ? history.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="text-center text-muted">{history.length - index}</td>
                                                    <td>{moment(item.date).format('DD-MM-YY')}</td>
                                                    <td>
                                                        <span className="font-weight-bold">{item.description || 'General'}</span>
                                                        <small className="text-muted ml-2">({item.payMode})</small>
                                                    </td>
                                                    <td className="text-right text-danger">{item.type === 'Credit' ? item.amount.toLocaleString() : '-'}</td>
                                                    <td className="text-right text-success">{item.type === 'Payment' ? item.amount.toLocaleString() : '-'}</td>
                                                    <td className="text-right font-weight-bold">
                                                        ₹ {(item.runningBal || 0).toLocaleString()}
                                                    </td>
                                                    <td className="text-center">
                                                        <i className="fas fa-times-circle text-gray cursor-pointer" onClick={() => deleteTransaction(item)}></i>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="7" className="text-center py-4 text-muted">No transactions found.</td></tr>
                                            )}

                                            {liveCustomerData && (
                                                <tr className="bg-light-yellow">
                                                    <td className="text-center">-</td>
                                                    <td>-</td>
                                                    <td className="font-italic text-muted">Opening Balance</td>
                                                    <td className="text-right">-</td>
                                                    <td className="text-right">-</td>
                                                    <td className="text-right font-weight-bold text-navy">₹ {(liveCustomerData.openingBalance || 0).toLocaleString()}</td>
                                                    <td></td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <style dangerouslySetInnerHTML={{
                __html: `
        .bg-navy { background: #001f3f; }
        .btn-navy { background: #001f3f; color: white; }
        .text-navy { color: #001f3f; }
        .text-xs { font-size: 0.75rem; }
        .cursor-pointer { cursor: pointer; }
        .bg-light-yellow { background-color: #fffdf0; }
      `}} />
        </div>
    );
}

export default Customerledger;